import { useState, useEffect } from 'react';
import { Frame } from '../atoms/Frame';
import { Clock } from '../atoms/Icons';
import { NumList } from '../atoms/NumList';
import { useT } from '../../../i18n/useT';
import { P, S, T, G } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

const COUNTDOWN_START = 60;

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  onCta: () => void;
}

export function ModerationStatus({ attempt }: TemplateProps) {
  const pendingText = useT('flow.moderation.pending');
  const [seconds, setSeconds] = useState(COUNTDOWN_START);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => {
      setSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);

  const rejectReasons = attempt?.reject_reasons ?? [];

  return (
    <Frame>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        {/* Uploaded screenshot preview (if available) */}
        {attempt?.artefact_path && (
          <div
            style={{
              width: 220,
              height: 280,
              borderRadius: 16,
              overflow: 'hidden',
              background: '#0E0A06',
              position: 'relative',
              boxShadow: '0 12px 30px rgba(20,12,8,0.18)',
              marginBottom: 24,
            }}
          >
            <img
              src={attempt.artefact_path}
              alt="Загруженное фото"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        {/* Clock icon in warm glass pill */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: P.warn,
            marginBottom: 16,
            ...G.warmTag,
          }}
        >
          <Clock size={28} />
        </div>

        <div
          style={{
            font: T.headline,
            color: S.label,
            marginBottom: 6,
          }}
        >
          {pendingText}
        </div>

        <div
          style={{
            font: T.footnote,
            color: S.secondaryLabel,
            lineHeight: 1.5,
            maxWidth: 240,
            marginBottom: 16,
          }}
        >
          Авто-проверка через{' '}
          <span
            style={{
              fontFamily: T.fontMono,
              fontWeight: 600,
              color: P.warn,
            }}
          >
            {seconds} сек.
          </span>
        </div>

        {/* Reject reasons from previous attempt (if any) */}
        {rejectReasons.length > 0 && (
          <div
            style={{
              width: '100%',
              maxWidth: 340,
              background: P.card,
              borderRadius: 16,
              padding: 14,
              border: `1px solid ${P.line}`,
              textAlign: 'left',
            }}
          >
            <div
              style={{
                font: T.caption,
                fontWeight: 700,
                color: S.label,
                marginBottom: 8,
              }}
            >
              Замечания предыдущей проверки:
            </div>
            <NumList items={rejectReasons} />
          </div>
        )}
      </div>
    </Frame>
  );
}
